import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtDateTime, CATEGORY_LABELS, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LabelPreview } from "@/pages/Labels";
import { ArrowLeft, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATE_BTN = [
  { k: "memenuhi", label: "Memenuhi", on: "bg-emerald-600 text-white border-emerald-600", off: "text-emerald-700 border-emerald-300 hover:bg-emerald-50" },
  { k: "tidak_memenuhi", label: "Tidak Memenuhi", on: "bg-red-600 text-white border-red-600", off: "text-red-700 border-red-300 hover:bg-red-50" },
  { k: "tidak_berlaku", label: "Tidak Berlaku", on: "bg-slate-500 text-white border-slate-500", off: "text-slate-600 border-slate-300 hover:bg-slate-100" },
];

export function TriState({ value, onChange, testid }) {
  return (
    <div className="flex gap-1">
      {STATE_BTN.map((b) => (
        <button key={b.k} type="button" data-testid={`${testid}-${b.k}`} onClick={() => onChange(b.k)}
          className={`text-xs px-2.5 py-1.5 rounded border font-medium transition-colors ${value === b.k ? b.on : "bg-transparent " + b.off}`}>
          {b.label}
        </button>
      ))}
    </div>
  );
}

function QcForm({ receipt, meta, onSaved }) {
  const { user } = useAuth();
  const groups = meta?.criteria_groups || [];
  const [results, setResults] = useState({});
  const [notes, setNotes] = useState("");
  const [findings, setFindings] = useState("");
  const [inspector, setInspector] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const insp = receipt.inspection;
    if (insp?.results) { setResults(insp.results); setNotes(insp.notes || ""); setFindings(insp.findings || ""); setInspector(insp.inspector || user?.name || ""); return; }
    const def = {};
    groups.forEach((g) => {
      const relevant = g.applies_to.includes(receipt.category);
      g.items.forEach((it) => { def[it.key] = relevant ? "" : "tidak_berlaku"; });
    });
    setResults(def);
  }, [receipt.id, meta]);

  const canEdit = ["qc", "admin"].includes(user?.role);
  const hasFail = Object.values(results).some((v) => v === "tidak_memenuhi");

  async function save() {
    const missing = Object.entries(results).filter(([, v]) => !v);
    if (missing.length) { toast.error("Masih ada kriteria yang belum dipilih."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/receipts/${receipt.id}/inspection`, { results, notes, findings, inspector });
      toast.success("Pemeriksaan QC tersimpan.");
      if (data.warning) toast.warning(data.warning);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-5">
      {hasFail && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> Terdapat kriteria <b>TIDAK MEMENUHI</b>. Arahkan bahan ke KARANTINA atau DITOLAK sesuai keputusan petugas berwenang.
        </div>
      )}
      {groups.map((g) => {
        const relevant = g.applies_to.includes(receipt.category);
        return (
          <Card key={g.key} className={`p-4 ${!relevant ? "opacity-70" : ""}`}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="font-heading font-bold text-sm">{g.label}</h4>
              {!relevant && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">tidak relevan untuk kategori ini</span>}
            </div>
            <div className="space-y-2.5">
              {g.items.map((it) => (
                <div key={it.key} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                  <span className="text-sm">{it.label}</span>
                  {canEdit ? <TriState testid={`qc-${it.key}`} value={results[it.key]} onChange={(v) => setResults({ ...results, [it.key]: v })} />
                    : <span className="text-xs font-medium">{results[it.key] || "-"}</span>}
                </div>
              ))}
            </div>
          </Card>
        );
      })}
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-xs">Catatan Temuan</Label><Textarea data-testid="qc-findings" value={findings} onChange={(e) => setFindings(e.target.value)} disabled={!canEdit} className="mt-1" /></div>
        <div><Label className="text-xs">Catatan QC</Label><Textarea data-testid="qc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit} className="mt-1" /></div>
      </div>
      <div><Label className="text-xs">Nama Pemeriksa</Label><Input data-testid="qc-inspector" value={inspector} onChange={(e) => setInspector(e.target.value)} disabled={!canEdit} className="mt-1 max-w-xs" /></div>
      {receipt.inspection && <div className="text-xs text-muted-foreground">Terakhir diperiksa: {fmtDateTime(receipt.inspection.inspected_at)} oleh {receipt.inspection.inspected_by}</div>}
      {canEdit && <Button data-testid="qc-save" onClick={save} disabled={saving}>Simpan Pemeriksaan</Button>}
    </div>
  );
}

function StatusActions({ receipt, reload }) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const canApprove = ["qc", "manajer", "admin"].includes(user?.role);
  const canCancel = ["gudang", "admin", "manajer"].includes(user?.role);

  async function change(status) {
    try { await api.post(`/receipts/${receipt.id}/status`, { status, reason }); toast.success(`Status diubah ke ${status.replace("_"," ")}`); setReason(""); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  return (
    <Card className="p-4">
      <h4 className="font-heading font-bold text-sm mb-3">Keputusan Status</h4>
      <Input data-testid="status-reason" placeholder="Alasan perubahan (wajib untuk karantina/tolak/batal)" value={reason} onChange={(e) => setReason(e.target.value)} className="mb-3" />
      <div className="flex flex-wrap gap-2">
        {canApprove && <Button data-testid="status-diterima" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => change("DITERIMA")}>Setujui (DITERIMA)</Button>}
        {canApprove && <Button data-testid="status-karantina" className="bg-yellow-500 hover:bg-yellow-600 text-black" onClick={() => change("KARANTINA")}>Karantina</Button>}
        {canApprove && <Button data-testid="status-ditolak" variant="destructive" onClick={() => change("DITOLAK")}>Tolak</Button>}
        {canCancel && <Button data-testid="status-dibatalkan" variant="outline" onClick={() => change("DIBATALKAN")}>Batalkan</Button>}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Hanya status DITERIMA yang memasukkan bahan ke stok tersedia. Persetujuan memerlukan pemeriksaan QC terisi.</p>
    </Card>
  );
}

export default function ReceiptDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { meta } = useAuth();
  const [r, setR] = useState(null);
  const [trace, setTrace] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/receipts/${id}`);
    setR(data);
    try { const t = await api.get(`/traceability/batch/${data.internal_batch}`); setTrace(t.data); } catch {}
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!r) return <div className="text-muted-foreground">Memuat...</div>;

  const DataRow = ({ k, v }) => (<div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v || "-"}</span></div>);

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/receipts")} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="flex items-center gap-3"><span className="batch-chip text-sm">{r.internal_batch}</span><StatusBadge status={r.status} /></div>
          <h1 className="font-heading text-2xl font-extrabold mt-1">{r.material_name}</h1>
          <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[r.category]} • {r.supplier_name}</p>
        </div>
        <Button variant="outline" data-testid="download-single-pdf" onClick={() => downloadFile(`/reports/mj-form-01.pdf?receipt_id=${r.id}`, `MJ-FORM-01_${r.internal_batch}.pdf`)}>
          <FileDown className="w-4 h-4 mr-1" /> Download Formulir MJ-FORM-01
        </Button>
      </div>

      <Tabs defaultValue="data">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="data" data-testid="tab-data">Data Penerimaan</TabsTrigger>
          <TabsTrigger value="qc" data-testid="tab-qc">Pemeriksaan QC</TabsTrigger>
          <TabsTrigger value="label" data-testid="tab-label">Label</TabsTrigger>
          <TabsTrigger value="trace" data-testid="tab-trace">Mutasi &amp; Traceability</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Riwayat</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-heading font-bold text-sm mb-2">Data Penerimaan</h4>
              <DataRow k="No. Tracking" v={r.tracking_no} />
              <DataRow k="Tgl Terima" v={fmtDate(r.tgl_terima)} />
              <DataRow k="Pemasok" v={`${r.supplier_code} — ${r.supplier_name}`} />
              <DataRow k="Bahan" v={`${r.material_code} — ${r.material_name}`} />
              <DataRow k="Kategori" v={CATEGORY_LABELS[r.category]} />
              <DataRow k="Batch Pemasok" v={r.supplier_batch} />
              <DataRow k="Jumlah Diterima" v={`${r.jumlah} ${r.unit}`} />
            </Card>
            <Card className="p-4">
              <h4 className="font-heading font-bold text-sm mb-2">Informasi Tambahan</h4>
              <DataRow k="Tgl Produksi" v={fmtDate(r.tgl_produksi)} />
              <DataRow k="Tgl Kedaluwarsa" v={fmtDate(r.expiry_date)} />
              <DataRow k="No. Surat Jalan" v={r.surat_jalan} />
              <DataRow k="Lokasi" v={r.location} />
              <DataRow k="Petugas Penerima" v={r.penerima} />
              <DataRow k="Dibuat Oleh" v={r.created_by} />
            </Card>
          </div>
          <StatusActions receipt={r} reload={load} />
        </TabsContent>

        <TabsContent value="qc"><QcForm receipt={r} meta={meta} onSaved={load} /></TabsContent>

        <TabsContent value="label"><LabelPreview receiptId={r.id} /></TabsContent>

        <TabsContent value="trace" className="space-y-4">
          {trace && (<>
            <Card className="p-4">
              <h4 className="font-heading font-bold text-sm mb-2">Ringkasan Stok Batch Ini</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><div className="text-2xl font-heading font-bold">{r.jumlah}</div><div className="text-xs text-muted-foreground">Diterima</div></div>
                <div><div className="text-2xl font-heading font-bold text-amber-600">{trace.qty_used}</div><div className="text-xs text-muted-foreground">Terpakai Produksi</div></div>
                <div><div className="text-2xl font-heading font-bold text-emerald-600">{trace.qty_remaining}</div><div className="text-xs text-muted-foreground">Sisa</div></div>
              </div>
            </Card>
            <Card className="p-4">
              <h4 className="font-heading font-bold text-sm mb-2">Batch Produksi yang Menggunakan Bahan Ini (Hilir)</h4>
              {trace.production_batches.length === 0 ? <div className="text-sm text-muted-foreground">Belum digunakan di produksi.</div> :
                trace.production_batches.map((p) => (
                  <div key={p.id} className="flex justify-between text-sm py-1.5 border-b border-slate-100 cursor-pointer hover:bg-accent/50" onClick={() => nav(`/production/${p.id}`)}>
                    <span className="batch-chip">{p.code}</span><span>{p.product_name}</span>
                  </div>
                ))}
            </Card>
            <Card className="p-4">
              <h4 className="font-heading font-bold text-sm mb-2">Mutasi Stok</h4>
              <MovTable moves={trace.movements} />
            </Card>
          </>)}
        </TabsContent>

        <TabsContent value="audit">
          <Card className="p-4">
            <h4 className="font-heading font-bold text-sm mb-2">Riwayat Perubahan (Audit Trail)</h4>
            {(trace?.audit || []).map((a) => (
              <div key={a.id} className="text-sm py-2 border-b border-slate-100 dark:border-slate-800">
                <div className="flex justify-between"><span className="font-medium">{a.action}</span><span className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span></div>
                <div className="text-xs text-muted-foreground">{a.user_name} ({a.role}) {a.reason ? `— ${a.reason}` : ""}</div>
              </div>
            ))}
            {(!trace?.audit || trace.audit.length === 0) && <div className="text-sm text-muted-foreground">Belum ada riwayat.</div>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function MovTable({ moves }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground border-b">
          <th className="py-2 font-medium">Waktu</th><th className="font-medium">Jenis</th><th className="font-medium">Masuk</th>
          <th className="font-medium">Keluar</th><th className="font-medium">Saldo</th><th className="font-medium">Referensi</th><th className="font-medium">User</th>
        </tr></thead>
        <tbody>
          {(moves || []).map((m) => (
            <tr key={m.id} className="border-b border-slate-100 dark:border-slate-800">
              <td className="py-2 text-xs">{fmtDateTime(m.created_at)}</td>
              <td><span className={`text-xs px-2 py-0.5 rounded font-medium ${m.type === "IN" ? "bg-emerald-100 text-emerald-700" : m.type === "OUT" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{m.type}</span></td>
              <td>{m.qty_in || "-"}</td><td>{m.qty_out || "-"}</td><td className="font-medium">{m.balance_after}</td>
              <td className="text-xs">{m.reference}</td><td className="text-xs">{m.user}</td>
            </tr>
          ))}
          {(!moves || moves.length === 0) && <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">Belum ada mutasi.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
